import { apiGet } from './client';
import { ApiResponse, PaginatedResponse } from '@tinyrewards/shared';

export type StatsPeriod = 'today' | 'week' | 'month' | 'lweek' | 'lmonth' | 'between' | 'all';

export interface RecipientRow {
    issuedBadgeID: string;
    recipientEmail: string;
    recipientName?: string;
    issuedDate: string;
    expiryDate?: string;
    claimStatus: string;
}

export interface RevokedRow {
    issuedBadgeID: string;
    recipientEmail: string;
    recipientName?: string;
    issuedDate: string;
    revokedDate: string;
}

export interface StatPoint {
    date: string;
    issued: number;
    revoked: number;
}

export interface StatsSummary {
    badgeID: string;
    period: StatsPeriod;
    totalIssued: number;
    totalRevoked: number;
    stats: StatPoint[];
}

function qs(badgeID: string, period: StatsPeriod, page?: number, from?: string, to?: string): string {
    const params = new URLSearchParams({ badgeID, period });
    if (page) params.set('page', String(page));
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return params.toString();
}

export function getTotal(badgeID: string): Promise<ApiResponse<{ badgeID: string; totalBadgesIssued: number }>> {
    return apiGet(`/api/stats/total?badgeID=${encodeURIComponent(badgeID)}`);
}

export function getRecipients(badgeID: string, period: StatsPeriod = 'all', page = 1): Promise<PaginatedResponse<RecipientRow>> {
    return apiGet<RecipientRow[]>(`/api/stats/recipients?${qs(badgeID, period, page)}`) as Promise<PaginatedResponse<RecipientRow>>;
}

export function getRevoked(badgeID: string, period: StatsPeriod = 'all', page = 1): Promise<PaginatedResponse<RevokedRow>> {
    return apiGet<RevokedRow[]>(`/api/stats/revoked?${qs(badgeID, period, page)}`) as Promise<PaginatedResponse<RevokedRow>>;
}

export function getSummary(badgeID: string, period: StatsPeriod = 'all'): Promise<ApiResponse<StatsSummary>> {
    return apiGet(`/api/stats/summary?${qs(badgeID, period)}`);
}

// -------------------------------------------------------
// Global (admin) reporting — system-wide stats across all badges.
// -------------------------------------------------------

export interface GlobalBadgeRow {
    badgeID: string;
    badgeName: string;
    issuerName: string | null;
    issued: number;
    revoked: number;
}

export interface GlobalTimelinePoint {
    date: string;
    issued: number;
    revoked: number;
}

export interface GlobalStats {
    period: StatsPeriod;
    totals: { activeUsers: number; activeBadges: number; issued: number; revoked: number };
    byBadge: GlobalBadgeRow[];
    timeline: GlobalTimelinePoint[];
}

export function getGlobalStats(period: StatsPeriod = 'all', from?: string, to?: string): Promise<ApiResponse<GlobalStats>> {
    const params = new URLSearchParams({ period });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiGet(`/api/admin/stats/global?${params.toString()}`);
}
